import Button from 'common/components/Button'
import Skeleton from 'common/components/Skeleton'
import Typography from 'common/components/Typography'
import { WEB_DESKTOP_RALALI_URL } from 'common/constants'
import theme from 'common/theme'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const FAQSection = styled.section`
  padding: 56px 24px;
  max-width: 1180px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 48px;
  align-items: flex-start;
  background: #ffffff;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`

const FAQInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FAQLabel = styled(Typography)`
  ${theme.typography.rlabel}
  letter-spacing: 2px;
  text-transform: uppercase;
`

const FAQTitle = styled(Typography)`
  font-weight: 700;
  color: ${theme.colors.black};
`

const FAQSubtitle = styled(Typography)`
  ${theme.typography.rparagraph}
  color: ${theme.colors.grayDark};
`

const FAQCTA = styled(Button)`
  width: fit-content;
  border-radius: 999px;
  padding: 10px 24px;
`

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const FAQItem = styled.div`
  border-bottom: 1px solid ${theme.colors.grayLight};
  &:last-child {
    border-bottom: none;
  }
`

const FAQHeader = styled.div`
  padding: 16px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const FAQQuestionContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`

const FAQNumber = styled.span`
  color: ${theme.colors.red};
  font-weight: bold;
  margin-right: 12px;
  font-size: 18px;
`

const FAQQuestion = styled.h3`
  font-size: 16px;
  color: ${theme.colors.black};
  margin: 0;
  font-weight: 600;
`

const FAQToggleIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.black};
  font-size: 18px;
  font-weight: bold;
  transition: transform 0.3s ease;
  transform: ${(props) =>
    props.isOpen ? 'rotate(45deg)' : 'rotate(0deg)'};
`

const FAQAnswer = styled.div`
  padding: ${(props) =>
    props.isOpen ? '0 16px 16px 16px' : '0 16px'};
  max-height: ${(props) => (props.isOpen ? '200px' : '0')};
  overflow: hidden;
  transition: all 0.3s ease;
  color: ${theme.colors.grayDark};
  line-height: 1.6;
`

interface IFAQItemData {
  question: string
  answer: string
}

interface IFAQComponentProps {
  data?: {
    title?: string
    items?: IFAQItemData[]
  }
}

function FAQComponent({ data }: IFAQComponentProps): JSX.Element {
  const { t } = useTranslation('home')
  const [openItems, setOpenItems] = useState({})
  const hasItems = Boolean(data?.items?.length)

  useEffect(() => null, [data])

  const toggleFAQ = (index) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  const handleContactUs = () => {
    window.open(`${WEB_DESKTOP_RALALI_URL}/hubungikami`, '_blank')
  }

  const renderSkeletons = () =>
    new Array(4)
      .fill(null)
      .map((_, index) => (
        <Skeleton
          key={`faq-skel-${index + 1}`}
          width="100%"
          height="60px"
        />
      ))

  return (
    <FAQSection>
      <FAQInfo>
        {hasItems ? (
          <>
            <FAQLabel type="rlabel" color="red">
              {t('FAQ_LABEL')}
            </FAQLabel>
            <FAQTitle type="bh2">
              {data?.title || t('FAQ_DEFAULT_TITLE')}
            </FAQTitle>
            <FAQSubtitle>{t('FAQ_SUBTITLE')}</FAQSubtitle>
            <FAQCTA
              onClick={handleContactUs}
              color="red"
              type="button"
            >
              {t('FAQ_CONTACT_TEAM')}
            </FAQCTA>
          </>
        ) : (
          <>
            <Skeleton width="140px" height="20px" />
            <Skeleton width="280px" height="36px" />
            <Skeleton width="220px" height="16px" />
            <Skeleton width="160px" height="40px" />
          </>
        )}
      </FAQInfo>
      <FAQList>
        {!hasItems && renderSkeletons()}
        {hasItems &&
          data.items.map((faq, index) => (
            <FAQItem
              key={`faq-item-${String(index + 1)}`}
              data-testid={`faq-item-${index + 1}`}
            >
              <FAQHeader onClick={() => toggleFAQ(index)}>
                <FAQQuestionContainer>
                  <FAQNumber>
                    {String(index + 1).padStart(2, '0')}
                  </FAQNumber>
                  <FAQQuestion>{faq.question}</FAQQuestion>
                </FAQQuestionContainer>
                <FAQToggleIcon isOpen={openItems[index]}>
                  +
                </FAQToggleIcon>
              </FAQHeader>
              <FAQAnswer isOpen={openItems[index]}>
                <Typography type="rparagraph">
                  {faq.answer}
                </Typography>
              </FAQAnswer>
            </FAQItem>
          ))}
      </FAQList>
    </FAQSection>
  )
}

export default FAQComponent
